/**
 * 端末の設定
 *
 * 【学校モードが毎回戻っていた】
 * 賭け事を出さない設定は盤面の画面が持っていたので、
 * **開き直すたびに「賭け事あり」に戻っていた。**
 * 学校で配る端末や、子どもに渡す端末で毎回選び直させるのは、
 * 選ばせているようで選ばせていない。端末に覚えさせる。
 *
 * 【アカウントの設定が端末より強い】
 * docs/08 §7。未成年向けの既定（`minor_mode`）が立っているアカウントで
 * 入っているあいだは、端末の設定に関わらず賭け事を出さない。
 * 端末を借りて遊ぶこともあるので、**人に付く設定のほうを強くする**。
 */

const KEY = 'tenka:wagers';

const store = (): Storage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
};

/**
 * 端末の設定。既定は「賭け事あり」。
 *
 * 既定を OFF にはしない — 賭場・借銭・見物は `docs/06 §8〜10` の
 * 遊びの一部で、外すと別のゲームになる。外したい場に外せることが大事で、
 * 誰にでも外れているのが正しいわけではない。
 */
export function loadWagers(): boolean {
  try {
    return store()?.getItem(KEY) !== '0';
  } catch {
    return true;
  }
}

export function saveWagers(enabled: boolean): void {
  try {
    store()?.setItem(KEY, enabled ? '1' : '0');
  } catch {
    // 覚えられないだけ。その場では効く
  }
}

/**
 * 実際に賭け事を出すか。
 *
 * `minorMode` が立っていれば端末の設定は見ない。
 * **人に付く設定のほうを強くする**（端末を借りて遊ぶこともある）。
 */
export const wagersAllowed = (device: boolean, minorMode: boolean): boolean =>
  minorMode ? false : device;

/** いまの設定が何によって決まっているか。画面で理由を出すために使う */
export const wagersReason = (minorMode: boolean): 'account' | 'device' =>
  minorMode ? 'account' : 'device';

// ---------------------------------------------------------------------------
// 音
// ---------------------------------------------------------------------------

/**
 * 音の設定。
 *
 * **効果音と曲を分けて持つ。** 教室では曲だけ切りたいことがあるし、
 * 一人で遊ぶときは曲だけ欲しいこともある。まとめて1つの切替にすると、
 * どちらかを諦めさせることになる。
 *
 * 既定は**両方とも入**にしてある。`docs/04` の「無音でも成立すること」は
 * 「音が無くても遊べる」という作りの話であって、
 * 「既定を無音にする」という意味ではない。
 * なお、どちらにしてもブラウザは人が画面に触るまで音を出さない。
 */
export interface SoundPrefs {
  readonly sfx: boolean;
  readonly music: boolean;
  /** 0〜1 */
  readonly volume: number;
}

export const DEFAULT_SOUND: SoundPrefs = Object.freeze({ sfx: true, music: true, volume: 0.7 });

const SOUND_KEY = 'tenka:sound';

export function loadSound(): SoundPrefs {
  try {
    const raw = store()?.getItem(SOUND_KEY);
    if (!raw) return DEFAULT_SOUND;
    const p = JSON.parse(raw) as Partial<SoundPrefs>;
    return {
      sfx: typeof p.sfx === 'boolean' ? p.sfx : DEFAULT_SOUND.sfx,
      music: typeof p.music === 'boolean' ? p.music : DEFAULT_SOUND.music,
      // 壊れた値で無音や爆音にしない
      volume:
        typeof p.volume === 'number' && Number.isFinite(p.volume)
          ? Math.min(1, Math.max(0, p.volume))
          : DEFAULT_SOUND.volume,
    };
  } catch {
    return DEFAULT_SOUND;
  }
}

export function saveSound(p: SoundPrefs): void {
  try {
    store()?.setItem(SOUND_KEY, JSON.stringify(p));
  } catch {
    // 覚えられないだけ。その場では効く
  }
}
